/**
 * demoMode.ts — Demo-Modus-Service (W3.1)
 *
 * Verwaltet den Demo-Modus-Zustand via sessionStorage.
 * Demo-Modus endet automatisch beim Tab-Schliessen (sessionStorage-Scope).
 *
 * Fix Demo-Blocker (W0.4-Kompatibilitaet): activateDemo/deactivateDemo
 * synchronisieren zusaetzlich den authStore, damit der Auth-Guard
 * Demo-User nicht zurueck zu /login redirected.
 */
import { useAuthStore } from '../stores/authStore'

const DEMO_MODE_KEY = 'ht_demo_mode'
const DEMO_TOKEN_KEY = 'ht_demo_token'

/**
 * Gibt true zurueck wenn Demo-Modus aktiv ist.
 */
export function isDemoMode(): boolean {
  return sessionStorage.getItem(DEMO_MODE_KEY) === 'true'
}

/**
 * Gibt true zurueck, wenn ein Demo-API-Token konfiguriert ist (VITE_DEMO_API_TOKEN).
 * Ohne Token laufen alle Requests im Demo-Modus unauthentifiziert — der Modus
 * waere also funktionslos.
 */
export function isDemoConfigured(): boolean {
  return (import.meta.env.VITE_DEMO_API_TOKEN ?? '').trim().length > 0
}

/**
 * Aktiviert den Demo-Modus.
 * Setzt ht_demo_mode=true und ht_demo_token in sessionStorage
 * und markiert den authStore als authentifiziert (Demo-User).
 *
 * Guard: Ohne konfigurierten VITE_DEMO_API_TOKEN wird NICHT aktiviert — sonst
 * wuerde die bestehende (echte) Session ueberschrieben und alle Requests liefen
 * ohne Authorization-Header. Rueckgabewert signalisiert den Erfolg.
 */
export function activateDemo(): boolean {
  const token = import.meta.env.VITE_DEMO_API_TOKEN
  if (!isDemoConfigured()) return false
  sessionStorage.setItem(DEMO_MODE_KEY, 'true')
  sessionStorage.setItem(DEMO_TOKEN_KEY, token)
  useAuthStore.getState().setAuthenticated(true)
  return true
}

/**
 * Deaktiviert den Demo-Modus.
 * Leert die Demo-spezifischen sessionStorage-Keys
 * und setzt den authStore zurueck.
 */
export function deactivateDemo(): void {
  sessionStorage.removeItem(DEMO_MODE_KEY)
  sessionStorage.removeItem(DEMO_TOKEN_KEY)
  useAuthStore.getState().clearAuth()
}

/**
 * Gibt den Demo-API-Token zurueck (nur wenn Demo-Modus aktiv).
 */
export function getDemoToken(): string | null {
  if (!isDemoMode()) return null
  return sessionStorage.getItem(DEMO_TOKEN_KEY)
}
